import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApprovePermitDto,
  CancelInspectionDto,
  CompleteInspectionDto,
  InspectionResultInput,
  InspectionRow,
  InspectionTemplateView,
  InspectionView,
  InspectorOption,
  PermitApplicationRow,
  PermitApplicationView,
  PermitRow,
  PermitTypeView,
  SaveInspectionTemplateDto,
  SavePermitTypeDto,
  ScheduleInspectionDto,
  UpdateInspectionDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useOptionalScope } from '../providers/ScopeProvider';

/**
 * Permits & licensing and inspections, staff side. Every route is under the
 * active municipality; the API checks the permission there and the module
 * entitlement on every call.
 */

const permitsBase = (lg: string) => `/local-governments/${lg}/permits`;
const inspectionsBase = (lg: string) => `/local-governments/${lg}/inspections`;

export const permitKeys = {
  all: ['permits'] as const,
  status: (lg: string) => ['permits', 'status', lg] as const,
  types: (lg: string) => ['permits', 'types', lg] as const,
  applications: (lg: string, status: string) => ['permits', 'applications', lg, status] as const,
  application: (lg: string, id: string) => ['permits', 'application', lg, id] as const,
  issued: (lg: string) => ['permits', 'issued', lg] as const,
};

export const inspectionKeys = {
  all: ['inspections'] as const,
  status: (lg: string) => ['inspections', 'status', lg] as const,
  templates: (lg: string) => ['inspections', 'templates', lg] as const,
  inspectors: (lg: string) => ['inspections', 'inspectors', lg] as const,
  list: (lg: string, from: string, to: string, inspector: string) =>
    ['inspections', 'list', lg, from, to, inspector] as const,
  one: (lg: string, id: string) => ['inspections', 'one', lg, id] as const,
};

/** The active scope's municipality, or null in a company or platform scope. */
export function useActiveMunicipality(): string | null {
  const scope = useOptionalScope();
  return scope?.active?.kind === 'local_government' ? scope.active.id : null;
}

/**
 * Whether the active municipality has each module. Off until proven on: while
 * loading, or without the permission to ask, a module is treated as absent, so
 * nothing new appears for a town whose plan does not include it.
 */
export function useModuleSwitches(): {
  permits: boolean;
  inspections: boolean;
  /** The switch is being asked and has not answered yet (not merely off). */
  permitsPending: boolean;
  inspectionsPending: boolean;
} {
  const scope = useOptionalScope();
  const lg = useActiveMunicipality();
  const canPermits = Boolean(lg && scope?.can('permit:read'));
  const canInspections = Boolean(lg && scope?.can('inspection:read'));
  const permits = useQuery({
    queryKey: permitKeys.status(lg ?? ''),
    queryFn: async () =>
      (await api.get<{ enabled: boolean }>(`${permitsBase(lg as string)}/status`)).data,
    enabled: canPermits,
    staleTime: 5 * 60_000,
    meta: { silent: true },
  });
  const inspections = useQuery({
    queryKey: inspectionKeys.status(lg ?? ''),
    queryFn: async () =>
      (await api.get<{ enabled: boolean }>(`${inspectionsBase(lg as string)}/status`)).data,
    enabled: canInspections,
    staleTime: 5 * 60_000,
    meta: { silent: true },
  });
  return {
    permits: canPermits && permits.data?.enabled === true,
    inspections: canInspections && inspections.data?.enabled === true,
    permitsPending: canPermits && permits.isLoading,
    inspectionsPending: canInspections && inspections.isLoading,
  };
}

/** Fetch a PDF or document through the session and hand it to the browser. */
export async function downloadBlob(path: string, filename: string): Promise<void> {
  const { data } = await api.get<Blob>(path, { responseType: 'blob' });
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ── Permits ───────────────────────────────────────────────────────────────────

export function usePermitTypes(lg: string | null) {
  return useQuery({
    queryKey: permitKeys.types(lg ?? ''),
    queryFn: async () =>
      (await api.get<PermitTypeView[]>(`${permitsBase(lg as string)}/types`)).data,
    enabled: Boolean(lg),
  });
}

export function useSavePermitType(lg: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...dto }: SavePermitTypeDto & { id?: string }) =>
      id
        ? (await api.put<PermitTypeView>(`${permitsBase(lg as string)}/types/${id}`, dto)).data
        : (await api.post<PermitTypeView>(`${permitsBase(lg as string)}/types`, dto)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: permitKeys.types(lg ?? '') }),
  });
}

export function usePermitApplications(lg: string | null, status: string) {
  return useQuery({
    queryKey: permitKeys.applications(lg ?? '', status),
    queryFn: async () =>
      (
        await api.get<PermitApplicationRow[]>(`${permitsBase(lg as string)}/applications`, {
          params: status ? { status } : {},
        })
      ).data,
    enabled: Boolean(lg),
  });
}

export function usePermitApplication(lg: string | null, id: string | undefined) {
  return useQuery({
    queryKey: permitKeys.application(lg ?? '', id ?? ''),
    queryFn: async () =>
      (await api.get<PermitApplicationView>(`${permitsBase(lg as string)}/applications/${id}`))
        .data,
    enabled: Boolean(lg && id),
  });
}

export type PermitDecision =
  | { action: 'review' }
  | { action: 'request-info'; message: string }
  | { action: 'payment'; reference: string }
  | ({ action: 'approve' } & ApprovePermitDto)
  | { action: 'refuse'; reason: string };

export function usePermitDecision(lg: string | null, id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ action, ...body }: PermitDecision) =>
      (
        await api.post<PermitApplicationView>(
          `${permitsBase(lg as string)}/applications/${id}/${action}`,
          body,
        )
      ).data,
    onSuccess: (view) => {
      qc.setQueryData(permitKeys.application(lg ?? '', view.id), view);
      void qc.invalidateQueries({ queryKey: permitKeys.all });
    },
  });
}

export function useIssuedPermits(lg: string | null) {
  return useQuery({
    queryKey: permitKeys.issued(lg ?? ''),
    queryFn: async () => (await api.get<PermitRow[]>(`${permitsBase(lg as string)}/issued`)).data,
    enabled: Boolean(lg),
  });
}

export function useRevokePermit(lg: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await api.post<PermitRow>(`${permitsBase(lg as string)}/issued/${id}/revoke`, { reason }))
        .data,
    onSuccess: () => qc.invalidateQueries({ queryKey: permitKeys.all }),
  });
}

export function downloadPermit(lg: string, permitId: string, number: string, locale: 'en' | 'fr') {
  return downloadBlob(
    `${permitsBase(lg)}/issued/${permitId}/document?locale=${locale}`,
    `${number}-${locale}.pdf`,
  );
}

export function downloadApplicationDocument(
  lg: string,
  applicationId: string,
  documentId: string,
  filename: string,
) {
  return downloadBlob(
    `${permitsBase(lg)}/applications/${applicationId}/documents/${documentId}`,
    filename,
  );
}

// ── Inspections ───────────────────────────────────────────────────────────────

export function useInspectionTemplates(lg: string | null) {
  return useQuery({
    queryKey: inspectionKeys.templates(lg ?? ''),
    queryFn: async () =>
      (await api.get<InspectionTemplateView[]>(`${inspectionsBase(lg as string)}/templates`)).data,
    enabled: Boolean(lg),
  });
}

export function useSaveInspectionTemplate(lg: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...dto }: SaveInspectionTemplateDto & { id?: string }) =>
      id
        ? (
            await api.put<InspectionTemplateView>(
              `${inspectionsBase(lg as string)}/templates/${id}`,
              dto,
            )
          ).data
        : (
            await api.post<InspectionTemplateView>(
              `${inspectionsBase(lg as string)}/templates`,
              dto,
            )
          ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: inspectionKeys.templates(lg ?? '') }),
  });
}

export function useInspectors(lg: string | null, enabled = true) {
  return useQuery({
    queryKey: inspectionKeys.inspectors(lg ?? ''),
    queryFn: async () =>
      (await api.get<InspectorOption[]>(`${inspectionsBase(lg as string)}/inspectors`)).data,
    enabled: Boolean(lg) && enabled,
  });
}

export function useInspections(
  lg: string | null,
  range: { from: string; to: string },
  inspectorId = '',
) {
  return useQuery({
    queryKey: inspectionKeys.list(lg ?? '', range.from, range.to, inspectorId),
    queryFn: async () =>
      (
        await api.get<InspectionRow[]>(inspectionsBase(lg as string), {
          params: { ...range, ...(inspectorId ? { inspectorId } : {}) },
        })
      ).data,
    enabled: Boolean(lg),
  });
}

export function useInspection(lg: string | null, id: string | undefined) {
  return useQuery({
    queryKey: inspectionKeys.one(lg ?? '', id ?? ''),
    queryFn: async () =>
      (await api.get<InspectionView>(`${inspectionsBase(lg as string)}/${id}`)).data,
    enabled: Boolean(lg && id),
  });
}

export function useScheduleInspection(lg: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: ScheduleInspectionDto) =>
      (await api.post<InspectionView>(inspectionsBase(lg as string), dto)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: inspectionKeys.all }),
  });
}

export type InspectionAction =
  | { action: 'update'; body: UpdateInspectionDto }
  | { action: 'cancel'; body: CancelInspectionDto }
  | { action: 'start' }
  | { action: 'results'; body: { results: InspectionResultInput[]; summary?: string | null } }
  | { action: 'complete'; body: CompleteInspectionDto }
  | { action: 'refer' };

export function useInspectionAction(lg: string | null, id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: InspectionAction) => {
      const base = `${inspectionsBase(lg as string)}/${id}`;
      switch (input.action) {
        case 'update':
          return (await api.patch<InspectionView>(base, input.body)).data;
        case 'results':
          return (await api.put<InspectionView>(`${base}/results`, input.body)).data;
        case 'start':
        case 'refer':
          return (await api.post<InspectionView>(`${base}/${input.action}`, {})).data;
        default:
          return (await api.post<InspectionView>(`${base}/${input.action}`, input.body)).data;
      }
    },
    onSuccess: (view) => {
      qc.setQueryData(inspectionKeys.one(lg ?? '', view.id), view);
      void qc.invalidateQueries({ queryKey: ['inspections', 'list'] });
    },
  });
}

export function useUploadInspectionPhoto(lg: string | null, id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemKey, file }: { itemKey: string; file: File }) => {
      const form = new FormData();
      form.append('photo', file);
      form.append('itemKey', itemKey);
      form.append('clientId', crypto.randomUUID());
      return (await api.post<InspectionView>(`${inspectionsBase(lg as string)}/${id}/photos`, form))
        .data;
    },
    onSuccess: (view) => qc.setQueryData(inspectionKeys.one(lg ?? '', view.id), view),
  });
}

export function downloadInspectionReport(
  lg: string,
  id: string,
  reference: string,
  locale: 'en' | 'fr',
) {
  return downloadBlob(
    `${inspectionsBase(lg)}/${id}/report?locale=${locale}`,
    `${reference}-${locale}.pdf`,
  );
}

/** Dollars from cents, in the reader's locale. */
export function formatFee(cents: number, language: string | undefined): string {
  return new Intl.NumberFormat(language?.startsWith('fr') ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(cents / 100);
}

/** `true` for an inspection row whose day is `day` (local time). */
export function onDay(row: Pick<InspectionRow, 'scheduledFor'>, day: Date): boolean {
  const d = new Date(row.scheduledFor);
  return (
    d.getFullYear() === day.getFullYear() &&
    d.getMonth() === day.getMonth() &&
    d.getDate() === day.getDate()
  );
}

/** Monday 00:00 of the week containing `date`, local time. */
export function weekStart(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return d;
}
